import type { ReactElement } from 'react';
import type { ExportFormatStructureNode } from '@capture/shared';

/**
 * Shared read-only structure-tree renderer. Replaces the two near-identical
 * private renderers (form-config BoundStructureView.ReadOnlyNodes and
 * export-formats SchemaImportModal.StructurePreview). Parameterized by the
 * caller's CSS classes + display flags so each call site keeps its exact look:
 * - BoundStructureView: showRequired + showArrayBadge, ro* classes.
 * - SchemaImportModal preview: nodeTestId="structure-preview-node", preview* classes.
 */
export interface StructureTreeViewClasses {
  node?: string;
  name?: string;
  badge?: string;
  required?: string;
}

export function StructureTreeView({
  nodes,
  depth = 0,
  classes,
  showRequired = false,
  showArrayBadge = false,
  nodeTestId,
}: {
  nodes: ExportFormatStructureNode[];
  depth?: number;
  classes: StructureTreeViewClasses;
  showRequired?: boolean;
  showArrayBadge?: boolean;
  nodeTestId?: string;
}): ReactElement {
  return (
    <>
      {nodes.map((node, i) => (
        <div key={`${node.name}-${i}`}>
          <div
            className={classes.node}
            style={{ marginLeft: depth * 16 }}
            {...(nodeTestId ? { 'data-testid': nodeTestId } : {})}
          >
            <span className={classes.name}>{node.name}</span>
            <span className={classes.badge}>
              {node.type === 'field' ? (node.dataType ?? 'raw') : node.type}
            </span>
            {showArrayBadge && node.type === 'collection' && (
              <span className={classes.badge}>array</span>
            )}
            {showRequired && node.type === 'field' && node.required && (
              <abbr className={classes.required} title="Required">
                *
              </abbr>
            )}
          </div>
          {node.children && node.children.length > 0 && (
            <StructureTreeView
              nodes={node.children}
              depth={depth + 1}
              classes={classes}
              showRequired={showRequired}
              showArrayBadge={showArrayBadge}
              nodeTestId={nodeTestId}
            />
          )}
        </div>
      ))}
    </>
  );
}
